import React, { useState, useEffect, useMemo, useCallback } from 'react';
import DetailedPageLayout from '../components/DetailedPageLayout';
import CustomAreaChart from '../../../components/Charts/CustomAreaChart';
import CustomBarChart from '../../../components/Charts/CustomBarChart';
import CustomDonutChart from '../../../components/Charts/CustomDonutChart';
import StatCard from '../components/ui/StatCard';
import SectionCard from '../components/ui/SectionCard';
import dashboardAPI from '../../../services/dashboardService';
import { Stethoscope, Activity, CalendarCheck } from 'lucide-react';

const DetailedMedicalPage = ({ isSubView = false, filters: externalFilters, onFilterChange: externalOnFilterChange }) => {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [localFilters, setLocalFilters] = useState({ timeRange: '30days', category: 'all' });

    const filters = externalFilters || localFilters;

    const fetchStats = useCallback(async (newFilters = filters) => {
        setLoading(true);
        try {
            const response = await dashboardAPI.getMedicalStats(newFilters);
            setStats(response.data);
        } catch (error) {
            console.error('Failed to fetch medical stats', error);
        } finally {
            setLoading(false);
        }
    }, [filters]);

    const onFilterChange = externalOnFilterChange || ((newFilters) => {
        setLocalFilters(newFilters);
        fetchStats(newFilters);
    });

    useEffect(() => {
        fetchStats(filters);
    }, [fetchStats, filters]);

    const statusData = useMemo(() => {
        if (!stats?.appointmentsByStatus) return [];
        return Object.entries(stats.appointmentsByStatus).map(([key, value]) => ({
            name: key.charAt(0).toUpperCase() + key.slice(1),
            value
        }));
    }, [stats?.appointmentsByStatus]);

    const graphData = stats?.graph_data || [];

    if (loading || !stats) {
        return <div className="sd-loading-message">Loading statistics...</div>;
    }

    return (
        <DetailedPageLayout
            title="Medical Analytics"
            onFilterChange={onFilterChange}
            filters={filters}
            isSubView={isSubView}
        >
            <div className="sd-grid sd-grid--kpi-users">
                <StatCard label="Total Appointments" value={stats.totalAppointments} icon={CalendarCheck} color="bg-rose-500" />
                <StatCard label="Active Clinics" value={stats.activeClinics} icon={Stethoscope} color="bg-teal-500" />
                <StatCard
                    label="Completion Rate"
                    value={`${(stats?.completionRate || 0).toFixed(1)}%`}
                    icon={Activity}
                    color="bg-amber-500"
                />
            </div>

            <div className="sd-grid sd-grid--split">
                <SectionCard title="Appointment Volume">
                    <CustomBarChart data={graphData} title="" />
                </SectionCard>
                <SectionCard title="Appointment Status">
                    <CustomDonutChart
                        data={statusData}
                        totalValue={stats.totalAppointments}
                        title=""
                    />
                </SectionCard>
            </div>

            <SectionCard title="Appointments Over Time">
                <CustomAreaChart data={graphData} color="#ef4444" title="" />
            </SectionCard>
        </DetailedPageLayout>
    );
};

export default DetailedMedicalPage;
